// @flow
import type { Epic } from '../../annotations';
import type { GetSuggestions } from '../../core/suggestions';
import type { SendAnalytics } from '../../core/ga';

import * as r from 'ramda';
import * as most from 'most';
import { select, combineEpics } from 'redux-most';
import * as signals from '../signals';
import * as messages from '../../domain/messages';
import * as selectors from '../../domain/selectors';
import {
  fromEagerPromise,
  switchMap,
  ignoreElements,
} from 'most-adjunct';
import * as mostc from '../../crosscutting/mostc';
import { Hook } from '../../core/constants';

const isQuerySearchable = (store) => (action) => r.gte(
  r.path([ 'payload', 'query', 'length' ], action),
  r.pipe(
    selectors.getDst,
    r.prop('minTldLength'),
  )(store.getState()),
);

export const searchEpic = (
  getSuggestions: GetSuggestions,
): Epic => (
  actions$,
  store
) => r.pipe(
  select(signals.SEARCH),
  mostc.filter(isQuerySearchable(store)),
  mostc.debounce(600),
  switchMap((action) => {
    const {
      payload: {
        query,
        filters,
      },
    } = action;
    const state = store.getState();

    const {
      brandId,
      exchangeRate,
      unspunTlds,
    } = selectors.getDst(state);
    const bundle = selectors.getBundleConfig(state);
    const browserSession = selectors.getBrowserSession(state);
    const usePPE = selectors.usePPE(state);
    const includeTax = selectors.includeTax(state);

    const fullQuery = query;

    const results$ = fromEagerPromise(() => getSuggestions(
      brandId,
      fullQuery,
      filters,
      exchangeRate,
      browserSession,
      bundle,
      usePPE,
      includeTax,
      unspunTlds,
    ));

    return most.merge(
      most.of(
        messages.searching(query),
      ),
      r.pipe(
        mostc.map(messages.searchResultsReceived),
        mostc.recoverWith(() => {
          return most.of(
            messages.searchFailure(
              new Error('Unable to get search results'),
            ),
          );
        }),
      )(results$),
    );
  }),
)(actions$);

export const sendGAEpic = (
  sendAnalytics: SendAnalytics,
): Epic => (actions$, store) => r.pipe(
  select(signals.SEARCH),
  mostc.filter(isQuerySearchable(store)),
  mostc.debounce(600),
  switchMap((action) => {
    const {
      meta: {
        source,
      },
    } = action;

    sendAnalytics({
      hitType: 'event',
      eventCategory: 'Search',
      eventAction: 'searched',
      dimension1: source, // Search Source
    });

    const startTime = new Date().getTime();

    return r.pipe(
      select(messages.SEARCH_RESULTS_RECEIVED),
      mostc.tap(() => {
        const endTime = new Date().getTime();

        const perfTime = endTime - startTime;

        sendAnalytics({
          hitType: 'timing',
          timingCategory: 'Request',
          timingVar: 'Get Suggestions',
          timingValue: perfTime,
        });
      }),
      ignoreElements,
    )(actions$);
  }),
)(actions$);

export const scrollEpic = (
  document: Document,
): Epic => r.pipe(
  select(signals.SEARCH),
  mostc.filter(r.pipe(
    r.path([ 'meta', 'source' ]),
    r.equals('sticky'),
  )),
  mostc.tap(() => {
    r.pipe(
      (n) => document.getElementsByClassName(n),
      r.head,
      r.unless(
        r.isNil,
        (node) => {
          node.scrollIntoView(true);
          return node;
        },
      )
    )('domain-suggestion-tool');
  }),
  ignoreElements,
);

export const sendEventEpic = (): Epic => r.pipe(
  select(signals.SEARCH),
  mostc.map((action) => {
    const {
      payload: {
        query,
        filters,
      },
    } = action;
    return signals.emit(Hook.SEARCHING, {
      query,
      filters,
    });
  }),
);

export default (
  getSuggestions: GetSuggestions,
  sendAnalytics: SendAnalytics,
  document: Document,
) => combineEpics([
  searchEpic(getSuggestions),
  sendGAEpic(sendAnalytics),
  sendEventEpic(),
  scrollEpic(document),
]);
